import { useState, useRef, useEffect } from 'react'
import type { ModuleComponentProps } from '@/lib/module-registry'
import type { BranchModuleData, BranchRepoConfig, BranchEntry } from '@/types/task'

// ─── types ────────────────────────────────────────────────────────────────────

interface BranchInputProps extends ModuleComponentProps<'branch'> {
  taskMode?: boolean // true = task level (edit entries), false/undefined = project config
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function emptyData(): BranchModuleData {
  return { repos: [], entries: [] }
}

function getEntry(entries: BranchEntry[], repo: string): BranchEntry | undefined {
  return entries.find((e) => e.repo === repo)
}

// ─── main component ───────────────────────────────────────────────────────────

export function BranchInputComponent({
  value,
  onChange,
  readOnly,
  taskMode,
}: BranchInputProps) {
  const data    = value ?? emptyData()
  const repos   = data.repos   ?? []
  const entries = data.entries ?? []

  function updateData(patch: Partial<BranchModuleData>) {
    onChange({ ...data, ...patch } as BranchModuleData)
  }

  // ── read-only mode ──────────────────────────────────────────────────────────

  if (readOnly) {
    if (repos.length === 0 || entries.length === 0) {
      return <span className="text-sm text-muted-foreground">—</span>
    }
    return (
      <div className="space-y-3">
        {repos.map((repo) => {
          const entry = getEntry(entries, repo.name)
          if (!entry) return null
          return (
            <div key={repo.name} className="space-y-1.5">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-semibold">{repo.name}</span>
                <span className="font-mono text-xs text-foreground">{entry.branch}</span>
                <span className="text-xs text-muted-foreground">from: {entry.from}</span>
              </div>
              <StageTrail flow={repo.flow} current={entry.stage} />
            </div>
          )
        })}
      </div>
    )
  }

  // ── task edit mode ──────────────────────────────────────────────────────────

  if (taskMode) {
    if (repos.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          Konfigurasi repository di Project → tab Modul → Branch Tracker terlebih dahulu.
        </p>
      )
    }
    return (
      <div className="space-y-4">
        {repos.map((repo) => {
          const entry = getEntry(entries, repo.name) ?? { repo: repo.name, branch: '', from: repo.source_branches[0] ?? '', stage: repo.flow[0] ?? '' }
          const hasContent = entry.branch.trim() !== ''

          function upsertEntry(patch: Partial<BranchEntry>) {
            const updated = { ...entry, ...patch }
            const rest    = entries.filter((e) => e.repo !== repo.name)
            // Hanya simpan jika branch diisi
            const next = updated.branch.trim() ? [...rest, updated] : rest
            updateData({ entries: next })
          }

          return (
            <div key={repo.name} className="space-y-2 rounded-xl border border-border px-4 py-3">
              <p className="text-xs font-semibold">{repo.name}</p>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Branch</label>
                  <input
                    type="text"
                    value={entry.branch}
                    onChange={(e) => upsertEntry({ branch: e.target.value })}
                    placeholder="feature/ONW-123-nama-fitur"
                    className="h-8 w-full rounded-md border border-input bg-background font-mono px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">From</label>
                  {repo.source_branches.length > 0 ? (
                    <select
                      value={entry.from}
                      onChange={(e) => upsertEntry({ from: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      {repo.source_branches.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={entry.from}
                      onChange={(e) => upsertEntry({ from: e.target.value })}
                      placeholder="development"
                      className="h-8 w-full rounded-md border border-input bg-background font-mono px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  )}
                </div>
              </div>

              {/* Stage selector */}
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Stage</label>
                <StageSelector
                  flow={repo.flow}
                  current={hasContent ? entry.stage : null}
                  onChange={(stage) => upsertEntry({ stage })}
                  disabled={!hasContent}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // ── config mode (project level) ─────────────────────────────────────────────

  return (
    <ConfigEditor
      repos={repos}
      onChange={(newRepos) => updateData({ repos: newRepos })}
    />
  )
}

// ─── stage sub-components ─────────────────────────────────────────────────────

function StageSelector({
  flow,
  current,
  onChange,
  disabled,
}: {
  flow:     string[]
  current:  string | null
  onChange: (stage: string) => void
  disabled?: boolean
}) {
  if (flow.length === 0) {
    return <span className="text-xs text-muted-foreground italic">Tidak ada stage</span>
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {flow.map((stage, i) => {
        const isActive = current === stage
        return (
          <span key={stage} className="flex items-center gap-1">
            {i > 0 && <span className="text-muted-foreground">→</span>}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(stage)}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : disabled
                    ? 'text-muted-foreground/40'
                    : 'border border-border text-muted-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {stage}
            </button>
          </span>
        )
      })}
    </div>
  )
}

function StageTrail({ flow, current }: { flow: string[]; current: string }) {
  const currentIdx = flow.indexOf(current)
  return (
    <div className="flex flex-wrap items-center gap-1">
      {flow.map((stage, i) => {
        const isDone   = i < currentIdx
        const isCurrent = i === currentIdx
        return (
          <span key={stage} className="flex items-center gap-1">
            {i > 0 && (
              <span className={`text-xs ${isDone || isCurrent ? 'text-primary' : 'text-muted-foreground/40'}`}>
                ──
              </span>
            )}
            <span
              className={`flex items-center gap-1 text-xs ${
                isCurrent
                  ? 'font-semibold text-primary'
                  : isDone
                    ? 'text-primary/60'
                    : 'text-muted-foreground/40'
              }`}
            >
              <span>{isCurrent ? '●' : isDone ? '●' : '○'}</span>
              <span>{stage}</span>
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ─── config editor ────────────────────────────────────────────────────────────

function ConfigEditor({
  repos,
  onChange,
}: {
  repos:    BranchRepoConfig[]
  onChange: (repos: BranchRepoConfig[]) => void
}) {
  const [addingRepo, setAddingRepo] = useState(false)
  const [newRepoName, setNewRepoName] = useState('')
  const repoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (addingRepo) repoInputRef.current?.focus()
  }, [addingRepo])

  function addRepo() {
    const name = newRepoName.trim()
    if (!name || repos.some((r) => r.name === name)) return
    onChange([...repos, { name, flow: [], source_branches: [] }])
    setNewRepoName('')
    setAddingRepo(false)
  }

  function removeRepo(name: string) {
    onChange(repos.filter((r) => r.name !== name))
  }

  function updateRepo(name: string, patch: Partial<BranchRepoConfig>) {
    onChange(repos.map((r) => (r.name === name ? { ...r, ...patch } : r)))
  }

  if (repos.length === 0 && !addingRepo) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Belum ada repository yang dikonfigurasi.</p>
        <button
          type="button"
          onClick={() => setAddingRepo(true)}
          className="flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
        >
          + Tambah Repository
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="divide-y divide-border rounded-xl border border-border">
        {repos.map((repo) => (
          <RepoConfigRow
            key={repo.name}
            repo={repo}
            onRemove={() => removeRepo(repo.name)}
            onUpdate={(patch) => updateRepo(repo.name, patch)}
          />
        ))}
      </div>

      {addingRepo ? (
        <div className="flex items-center gap-2">
          <input
            ref={repoInputRef}
            value={newRepoName}
            onChange={(e) => setNewRepoName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addRepo() }
              if (e.key === 'Escape') { setAddingRepo(false); setNewRepoName('') }
            }}
            onBlur={() => { if (!newRepoName.trim()) setAddingRepo(false) }}
            placeholder="nama repository"
            className="h-8 w-40 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            onClick={addRepo}
            className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary-hover"
          >
            Tambah
          </button>
          <button
            type="button"
            onClick={() => { setAddingRepo(false); setNewRepoName('') }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Batal
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAddingRepo(true)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
        >
          + Tambah Repository
        </button>
      )}
    </div>
  )
}

function RepoConfigRow({
  repo,
  onRemove,
  onUpdate,
}: {
  repo:     BranchRepoConfig
  onRemove: () => void
  onUpdate: (patch: Partial<BranchRepoConfig>) => void
}) {
  return (
    <div className="space-y-3 px-4 py-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{repo.name}</p>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          × Hapus
        </button>
      </div>

      {/* Flow stages */}
      <TagListEditor
        label="Flow (urutan stage)"
        placeholder="stage, e.g. dev"
        tags={repo.flow}
        onChange={(flow) => onUpdate({ flow })}
        hint="Isi urutan deployment, e.g. dev → qa → prod"
      />

      {/* Source branches */}
      <TagListEditor
        label="Source Branches"
        placeholder="branch, e.g. development"
        tags={repo.source_branches}
        onChange={(source_branches) => onUpdate({ source_branches })}
        hint="Branch asal yang bisa dipilih saat bikin branch baru"
      />
    </div>
  )
}

// ─── reusable tag list editor ─────────────────────────────────────────────────

function TagListEditor({
  label,
  placeholder,
  tags,
  onChange,
  hint,
}: {
  label:       string
  placeholder: string
  tags:        string[]
  onChange:    (tags: string[]) => void
  hint?:       string
}) {
  const [adding, setAdding] = useState(false)
  const [newVal, setNewVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adding) inputRef.current?.focus()
  }, [adding])

  function addTag() {
    const val = newVal.trim()
    if (!val || tags.includes(val)) return
    onChange([...tags, val])
    setNewVal('')
    setAdding(false)
  }

  return (
    <div className="space-y-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs">
            {tag}
            <button
              type="button"
              onClick={() => onChange(tags.filter((_, j) => j !== i))}
              className="ml-0.5 text-muted-foreground hover:text-destructive"
            >
              ×
            </button>
          </span>
        ))}
        {adding ? (
          <input
            ref={inputRef}
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addTag() }
              if (e.key === 'Escape') { setAdding(false); setNewVal('') }
            }}
            onBlur={() => { if (!newVal.trim()) setAdding(false) }}
            placeholder={placeholder}
            className="h-6 w-28 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
          >
            + Tambah
          </button>
        )}
      </div>
    </div>
  )
}
